import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { useEntityType, useNavigate } from 'hooks/index';
import { FORM_MODES, PATH_TO_ENTITY_MAP } from 'modules/campaigns/config';
import { fetchCampaignsGroupForEdit } from 'modules/campaigns/modules/campaigns-group-form/flows';
import { invalidateSubCountryData } from 'modules/campaigns/modules/common-campaign-form/actions';
import { useReportReachToGTM } from 'modules/campaigns/modules/common-campaign-form/components/ReachEstimator/hooks/useReportReachToGTM';
import { fetchCampaignForEdit, saveCampaign } from 'modules/campaigns/modules/common-campaign-form/flows';
import { fetchCampaignsForEdit } from 'modules/campaigns/modules/common-campaign-form/flows/fetchCampaignsForEdit';
import { generateEditCreativesPath, generateBulkCampaignEditorPath } from 'modules/campaigns/services/utils';
import { useAccount } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import { useMultiLayerPathParams } from 'modules/taboola-common-frontend-modules/multi-layer-drawer/hooks/useMultiLayerPathParams';
import { useBulkOperationsApi, useCampaignGroupApi } from 'services/api';
import { useSelectedCampaignUrl } from './useSelectedCampaignUrl';

const useCampaignEditorState = () => {
    const { mode, fetchedData: originalCampaign } = useFormDataContext();

    const isDuplicateMode = mode === FORM_MODES.DUPLICATE;

    const navigate = useNavigate();
    const { accountName } = useAccount();
    const dispatch = useDispatch();
    const entityType = useEntityType(PATH_TO_ENTITY_MAP);
    const { campaignId, formEntityIds } = useMultiLayerPathParams();
    const campaignGroupApi = useCampaignGroupApi();
    const bulkOperationsApi = useBulkOperationsApi();
    const selectedCampaignUrl = useSelectedCampaignUrl(mode);
    const formDataFetcher = useCallback(async () => {
        const isEntityEditorForm = !isEmpty(formEntityIds);
        if (isEntityEditorForm) {
            const selectedCampaignIds = formEntityIds.join(',');
            return dispatch(fetchCampaignsForEdit(accountName, selectedCampaignIds, bulkOperationsApi));
        }

        const campaign = await dispatch(fetchCampaignForEdit(accountName, campaignId, entityType));
        const { campaignGroupInfo } = campaign || {};
        if (campaignGroupInfo?.campaignGroupId) {
            const campaignGroup = await dispatch(
                fetchCampaignsGroupForEdit(accountName, campaignGroupInfo.campaignGroupId, entityType, campaignGroupApi)
            );
            return {
                ...campaign,
                campaignGroupInfo: {
                    campaignGroupId: campaignGroupInfo.campaignGroupId,
                    campaignsGroupBudgetDistributionMethod: campaignGroup.budgetDistributionMethod,
                },
            };
        }
        return campaign;
    }, [formEntityIds, dispatch, accountName, campaignId, entityType, bulkOperationsApi, campaignGroupApi]);
    const { submit } = useFormState({ formDataFetcher });
    const { reportReachToGTM } = useReportReachToGTM();
    const saveCampaignWrapper = useCallback(
        campaign =>
            dispatch(
                saveCampaign({
                    accountName,
                    originalCampaign,
                    editedCampaign: campaign,
                    entityType,
                    mode,
                })
            ),
        [accountName, originalCampaign, entityType, mode, dispatch]
    );
    const handleCampaignSubmitWrapper = useCallback(
        ({ shouldPopLastLayer } = {}) => {
            submit({
                saveFormData: saveCampaignWrapper,
                successUrl: selectedCampaignUrl,
                shouldPopLastLayer,
            });
            reportReachToGTM(originalCampaign.id);
        },
        [reportReachToGTM, submit, originalCampaign, selectedCampaignUrl, saveCampaignWrapper]
    );

    const handleSubmitAndEditCreativesWrapper = useCallback(
        ({ disableSubmit }) => {
            if (isDuplicateMode) {
                submit({
                    saveFormData: saveCampaignWrapper,
                    successUrl: response => {
                        const newCampaignId = response[0]?.id;
                        return generateEditCreativesPath({
                            accountId: accountName,
                            campaignId: newCampaignId,
                        });
                    },
                });
            } else {
                if (disableSubmit) {
                    navigate(generateEditCreativesPath({ accountId: accountName, campaignId }), { replaceForm: true });
                    return;
                }
                submit({
                    saveFormData: saveCampaignWrapper,
                    successUrl: generateEditCreativesPath({ accountId: accountName, campaignId }),
                });
                reportReachToGTM(originalCampaign.id);
            }
        },
        [
            reportReachToGTM,
            submit,
            originalCampaign,
            navigate,
            saveCampaignWrapper,
            accountName,
            campaignId,
            isDuplicateMode,
        ]
    );

    const handleSubmitAndBulkEditWrapper = useCallback(async () => {
        const duplicatedCampaigns = await submit({
            saveFormData: saveCampaignWrapper,
        });
        const successfulCampaignIds =
            duplicatedCampaigns?.filter(campaign => campaign && campaign.id)?.map(campaign => campaign.id) || [];

        if (successfulCampaignIds.length > 0) {
            const bulkEditPath = generateBulkCampaignEditorPath({
                accountId: accountName,
                formEntityIds: successfulCampaignIds,
            });
            navigate(bulkEditPath, { replaceForm: true });
        }

        reportReachToGTM(originalCampaign.id);
    }, [submit, saveCampaignWrapper, reportReachToGTM, accountName, originalCampaign, navigate]);

    //clearing resources
    useEffect(
        () => () => {
            dispatch(invalidateSubCountryData());
        },
        [dispatch]
    );

    return {
        submit: handleCampaignSubmitWrapper,
        submitAndEditCreatives: handleSubmitAndEditCreativesWrapper,
        submitAndBulkEdit: handleSubmitAndBulkEditWrapper,
    };
};

export default useCampaignEditorState;
